import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../api";
import "./SuccessPage.css";

type OrderStatus = "pending" | "accepted" | "played" | "declined";
type ProgressStep = 0 | 1 | 2;

interface OrderInfo {
  orderId: string;
  status: OrderStatus;
  searchQuery: string;
  amount: number;
  trackImage?: string | null;
  trackArtists?: string[];
  trackAlbum?: string;
  djSlug?: string;
  declineReason?: "pas_ambience" | "no_access" | "more_money";
  counterAmount?: number | null;
}

const POLLING_INTERVAL_MS = 5000;

const FreeSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order_id");
  const slug =
    searchParams.get("slug") || sessionStorage.getItem("lastDjSlug") || "";
  const [order, setOrder] = useState<OrderInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const getCompletedStep = (status: OrderStatus): ProgressStep => {
    if (status === "pending" || status === "declined") return 1;
    if (status === "accepted" || status === "played") return 2;
    return 0;
  };

  useEffect(() => {
    if (!orderId) {
      setErrorMsg("Missing order_id in URL");
      setLoading(false);
      return;
    }
    api
      .get<OrderInfo>(`/orders/order-status/${orderId}`)
      .then((res) => {
        setOrder(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching order:", err);
        setErrorMsg(err.response?.data?.msg || "Error while loading request");
        setLoading(false);
      });
  }, [orderId]);

  useEffect(() => {
    if (!order) return;
    if (["accepted", "played", "declined"].includes(order.status)) return;

    const id = setInterval(() => {
      api
        .get<OrderInfo>(`/orders/order-status/${order.orderId}`)
        .then((res) => {
          if (res.data.status !== order.status) {
            setOrder(res.data);
          }
        })
        .catch((err) => {
          console.error("Polling error:", err);
        });
    }, POLLING_INTERVAL_MS);

    return () => clearInterval(id);
  }, [order]);

  if (loading) {
    return (
      <div className="success-container">
        <p className="status-text">Submitting your request…</p>
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className="success-container">
        <p className="status-text error">{errorMsg}</p>
      </div>
    );
  }

  if (!order) return null;

  const {
    searchQuery,
    trackImage,
    trackArtists = [],
    status,
    declineReason,
  } = order;

  const completedStep = getCompletedStep(status);

  const getProgressWidth = () => {
    if (status === "declined") return "50%";
    if (completedStep === 0) return "33%";
    if (completedStep === 1) return "50%";
    return "100%";
  };

  const getDeclineReasonLabel = (
    r?: "pas_ambience" | "no_access" | "more_money"
  ): string | null => {
    if (!r) return null;
    switch (r) {
      case "pas_ambience":
        return "Not the vibe right now";
      case "no_access":
        return "The artist doesn’t have access to this song";
      case "more_money":
        // без намёков на оплату — просто нейтральный текст
        return "The artist requested a higher amount";
      default:
        return null;
    }
  };

  const reasonLabel = getDeclineReasonLabel(declineReason);

  return (
    <div className="success-container">
      <h1 className="success-heading">Track Request</h1>

      <div className="card">
        <div className="track-image-wrapper">
          {trackImage ? (
            <img src={trackImage} alt={searchQuery} className="track-image" />
          ) : (
            <div className="track-image-placeholder" />
          )}
        </div>

        <div className="track-info">
          <h2 className="track-title">{searchQuery}</h2>
          {trackArtists.length > 0 && (
            <p className="track-artists">{trackArtists.join(", ")}</p>
          )}
        </div>

        <div className="progress-container">
          <div className="progress-bar-bg" />

          {status === "declined" ? (
            <>
              <div
                className="progress-bar-fill green-half"
                style={{ width: "50%" }}
              />
              <div
                className="progress-bar-fill red-half"
                style={{ left: "50%", width: "50%" }}
              />
            </>
          ) : (
            <div
              className="progress-bar-fill"
              style={{ width: getProgressWidth() }}
            />
          )}

          <div className="progress-nodes">
            <div className={`node ${completedStep >= 0 ? "active" : ""}`}>
              <span className="node-label req-made">Request Made</span>
            </div>

            <div
              className={`node ${
                status === "pending"
                  ? "node-loading"
                  : completedStep >= 1
                  ? "active"
                  : ""
              }`}
            >
              <span className="node-label">Waiting for Reply</span>
            </div>

            <div
              className={`node ${
                status === "declined"
                  ? "declined-node"
                  : completedStep >= 2
                  ? "active"
                  : ""
              }`}
            >
              {status === "declined" ? (
                <span className="node-label req-dec">Request Declined</span>
              ) : (
                <span className="node-label music-accept-node">
                  Music Accepted
                </span>
              )}
            </div>
          </div>
        </div>

        {status === "declined" && (
          <div style={{ marginTop: 16, textAlign: "left" }}>
            <h3 style={{ color: "var(--color-primary)", textAlign: "center" }}>
              Your request was declined
            </h3>

            {reasonLabel && (
              <p
                style={{
                  color: "var(--color-text-light)",
                  textAlign: "center",
                }}
              >
                Reason:&nbsp;<strong>{reasonLabel}</strong>
              </p>
            )}
          </div>
        )}

        <div className="button-group">
          <button
            className="btn-make-another"
            onClick={() => {
              if (slug) {
                window.open(`/dj/${slug}/request`, "_blank");
              } else {
                window.history.back();
              }
            }}
          >
            Make another request
          </button>
        </div>
      </div>
    </div>
  );
};

export default FreeSuccessPage;
